import {useIntl} from 'react-intl';
import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {
  THUMBNAIL_SIZE_GRID,
  THUMBNAIL_SIZE_LIST,
  VIEW_TYPE,
  THUMBNAIL_SIZE_GRID_SELECTED,
  GRID_SIZE,
} from './const';

import {
  FileViewer,
  FilterProps,
  InfiniteScrollProps,
  ItemType,
  StudioFileItemGroup,
  StudioFileViewerProps,
  ViewId,
} from './FileViewer';
import {debounce, difference, throttle} from 'lodash';
import {PagingParams} from '../../types/dataService/PagingParams';
import {
  DEFAULT_FIELDS,
  DEFAULT_PAGE_PARAMS,
  DEFAULT_PAGE_PARAMS_LABEL,
  DEFAULT_PAGE_PARAMS_LIST,
  FIELDS_LIST,
  RSQLProps,
  useDatasetLoader,
} from './DatasetLoader';
import {TABLE_ROW_SIZE} from './const';
import {ITEMTYPE_TO_METADATA_MAP} from '../../store/dataService/datasetMetaDataUtil';
import {
  StudioDatasetViewerProvider,
  useStudioDatasetViewerContext,
} from './StudioDatasetViewerProvider';
import {BinaryRenderer, RendererClassificationProps, RendererProps} from './Renderer';
import {CategoryList} from '../../types/dataService/DatasetMetadata';
import {Dataset} from '../../types/dataService/Dataset';
import {GridOnItemsRenderedProps, ListOnItemsRenderedProps} from 'react-window';
import './DataReview.scss';
import {ListViewerVirtualized} from './FileViewer/ListViewer';

export type ScrollProps = {
  visibleItemIds: string[];
};

export type ItemStyleProps = {
  color?: string;
  backgroundColor?: string;
  borderColor?: string;
};

export type StudioDatasetViewerProps = {
  dataset?: Dataset;
  categories?: CategoryList;
  datasetVersion?: number;
  showLabels?: boolean;
  readOnly?: boolean;
  allowMultiselection?: boolean;
  busyItems?: string[];
  defaultFields?: string;

  // No idea what I am doing here, sorry
  width?: number;
  height?: number;

  // RSQL query
  filter?: RSQLProps[];

  itemRenderer?: (config: RendererProps | RendererClassificationProps) => React.ReactNode;
  selectionRenderer?: (
    config: RendererProps | RendererClassificationProps
  ) => React.ReactNode;

  onClearAll?: () => void;
  onChange?(files: File[]): void;
  onClick?(item: ItemType, target?: React.MouseEvent<HTMLDivElement, MouseEvent>): void;
  onDataChange?(item: ItemType[]): void;
  onScrollStop?: (args: ScrollProps) => void;
  getItemStyle?: (id: string) => void;
} & Pick<
  StudioFileViewerProps,
  | 'groupByKey'
  | 'view'
  | 'gridViewRender'
  | 'listViewRender'
  | 'labelViewRender'
  | 'labelHeader'
  | 'onFilter'
  | 'allowSelection'
>;

const VIRTUALIZED_IMAGE_ROW_HEIGHT = 100;
const VIRTUALIZED_IMAGE_COLUMN_WIDTH = 100;
const VIRTUALIZED_VIDEO_ROW_HEIGHT = 210;
const VIRTUALIZED_VIDEO_COLUMN_WIDTH = 210;

export const DEFAULT_GRID_PAGE_PARAMS = {
  ...DEFAULT_PAGE_PARAMS,
  size: 100, //should be dynamic depending on the screen size
};

export const getThumbSize = (view: ViewId, isSelected: boolean) =>
  view === 'list'
    ? THUMBNAIL_SIZE_LIST
    : isSelected
    ? THUMBNAIL_SIZE_GRID_SELECTED
    : THUMBNAIL_SIZE_GRID;

export const StudioDatasetViewer = ({
  dataset,
  categories,
  datasetVersion,
  showLabels = false,
  readOnly = false,
  allowMultiselection = true,
  groupByKey,
  filter,
  labelHeader,
  busyItems = [],
  defaultFields = DEFAULT_FIELDS,
  allowSelection,

  itemRenderer = BinaryRenderer,
  selectionRenderer = BinaryRenderer,

  gridViewRender,
  listViewRender = ListViewerVirtualized,
  labelViewRender,

  view = VIEW_TYPE.LABEL,

  onClearAll,
  onChange,
  onClick = () => {},
  onFilter = () => {},
  onDataChange = () => {},
  onScrollStop,
  height: contentHeight,
  width: contentWidth,
  getItemStyle = () => {},
}: StudioDatasetViewerProps) => {
  const intl = useIntl();
  const [pageParams, setPageParams] = useState<PagingParams>(DEFAULT_PAGE_PARAMS_LABEL);
  const [isSubView, setIsSubView] = useState<boolean>(false);

  const [fields, setFields] = useState<string>(defaultFields || DEFAULT_FIELDS);

  const {
    selections,
    setSelections: setSelelectionsContext,
    selectById,
    setBinaries,
    setAllBinaries,
    setLabels,
    setLabelMap,
    setStatus,
    labelMap,
    refreshCountBinary,
    refreshCountLabel,
  } = useStudioDatasetViewerContext();

  const {
    binaries,
    allBinaries,
    labels,
    labelMap: loaderLabelMap,
    isLoading,
    isInfiniteLoading,
    loadMoreBinaries,
    refreshBinaries,
    refreshLabels,
  } = useDatasetLoader({
    datasetId: dataset?.id,
    method: dataset?.purpose,
    categories,
    version: datasetVersion,
    pageParams,
    rsql: filter,
    fields,
    showLabels,
    view: isSubView ? VIEW_TYPE.GRID : view,
  });

  const fileItems: ItemType[] = useMemo(() => binaries?.list || [], [binaries]);

  const setSelections = useMemo(() => (readOnly ? () => {} : setSelelectionsContext), [
    readOnly,
    setSelelectionsContext,
  ]);

  const onListSelectAll = useCallback(
    (toggle: boolean, ids: string[] = []) => {
      if (ids.length) {
        setSelections(toggle ? selections.concat(ids) : difference(selections, ids));
      } else {
        setSelections(toggle ? fileItems.map(el => el.id) : []);
      }
    },
    [selections, fileItems, setSelections]
  );

  const toggleSelection = (id: string) => {
    if (allowMultiselection) {
      setSelections(
        selections.includes(id) ? difference(selections, [id]) : selections.concat(id)
      );
    } else {
      selectById(id);
    }
  };

  const onItemSelect = ({id, file}: ItemType) => {
    toggleSelection(String(id));
  };

  const onItemClick = (
    item: ItemType,
    e?: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    const {id} = item;
    onClick(item, e);

    if (!readOnly) {
      toggleSelection(String(id));
    }
  };

  const onLoadMore = () => {
    !isInfiniteLoading && loadMoreBinaries();
  };

  const onSort = (colId: string, dir: string) => {
    setPageParams({
      ...pageParams,
      sort: [ITEMTYPE_TO_METADATA_MAP[colId] as string, dir],
    });
  };

  const onScrollStopLocal = throttle((args: InfiniteScrollProps) => {
    // Return binary Ids that are visible
    if (binaries && onScrollStop) {
      if (view === VIEW_TYPE.GRID) {
        const gridArgs = args as GridOnItemsRenderedProps;
        const {
          visibleRowStartIndex,
          visibleRowStopIndex,
          visibleColumnStopIndex,
        } = gridArgs;
        const visibleColumns = visibleColumnStopIndex + 1;
        const visibleItemIds = binaries.list
          .slice(
            visibleRowStartIndex * visibleColumns,
            visibleRowStopIndex * visibleColumns
          )
          .map(el => el.id);
        onScrollStop({
          visibleItemIds,
        });
      } else {
        // list
        const listArgs = args as ListOnItemsRenderedProps;
        const {visibleStartIndex, visibleStopIndex} = listArgs;
        const visibleItemIds = binaries.list
          .slice(visibleStartIndex, visibleStopIndex)
          .map(el => el.id);
        onScrollStop({
          visibleItemIds,
        });
      }
    }
  }, 1500);

  const getGridSize = (width: number, height: number, rowOverScan: number = 1) => {
    return (Math.floor(height / GRID_SIZE) + rowOverScan) * Math.floor(width / GRID_SIZE);
  };

  const onLocalFilter = (filterOn: FilterProps | null, isLabelSubView: boolean) => {
    setIsSubView(isLabelSubView);
    const newPageParams = isLabelSubView
      ? {
          ...DEFAULT_GRID_PAGE_PARAMS,
          size: getGridSize(contentWidth || 0, contentHeight || 0),
        }
      : view === VIEW_TYPE.LIST || VIEW_TYPE.LABEL
      ? DEFAULT_PAGE_PARAMS_LABEL
      : DEFAULT_PAGE_PARAMS_LIST;
    setPageParams({...newPageParams});

    if (isLabelSubView) {
      setFields(defaultFields);
    } else {
      setFields(view === VIEW_TYPE.LABEL ? defaultFields : FIELDS_LIST);
    }
    onFilter(filterOn, isLabelSubView);
  };

  // Initialize infinite data loading
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useCallback(debounce(loadMoreBinaries, 1000), [
    binaries,
    view,
    loadMoreBinaries,
    isInfiniteLoading,
  ]);

  // Dynamic list sizing on view dimension changes for "LIST" view
  useMemo(() => {
    if (contentHeight && view === VIEW_TYPE.LIST && binaries?.list.length) {
      const size = Math.floor(contentHeight / TABLE_ROW_SIZE);
      size !== pageParams.size &&
        setPageParams({
          ...pageParams,
          page: 0,
          size,
        });
    }
    // eslint-disable-next-line
  }, [contentHeight, view, pageParams]);

  // Dynamic list sizing on view dimension changes for "GRID" view
  useEffect(() => {
    if (
      contentHeight &&
      contentWidth &&
      view === VIEW_TYPE.GRID &&
      binaries?.list.length
    ) {
      const columnCount = Math.floor(contentWidth / GRID_SIZE);
      const rowCount = Math.floor(contentHeight / GRID_SIZE);

      const size = columnCount * rowCount;
      if (size !== pageParams.size) {
        setPageParams({
          ...pageParams,
          page: 0,
          size,
        });
      }
    }
  }, [contentHeight, contentWidth, view, pageParams, binaries?.list.length]);

  //Reset Page defaults on "LABEL" view change
  useMemo(() => {
    view === VIEW_TYPE.LABEL && setPageParams({...DEFAULT_PAGE_PARAMS_LABEL});
  }, [view]);

  useMemo(() => {
    if (view) {
      setFields(view === VIEW_TYPE.LABEL ? defaultFields : FIELDS_LIST);
    }
  }, [view, defaultFields]);

  // Expose loaded data to the Context
  // TODO: remove this by putting loader one level up
  useMemo(() => setBinaries(binaries), [binaries, setBinaries]);
  useMemo(() => setAllBinaries(allBinaries), [allBinaries, setAllBinaries]);
  useMemo(() => setLabels(labels), [labels, setLabels]);
  useMemo(() => setLabelMap(loaderLabelMap), [loaderLabelMap, setLabelMap]);

  // eslint-disable-next-line
  useMemo(() => refreshCountBinary && refreshBinaries(), [refreshCountBinary]);
  // eslint-disable-next-line
  useMemo(() => refreshCountLabel && refreshLabels(), [refreshCountLabel]);

  useMemo(() => setStatus(isLoading ? 'LOADING' : 'IDLE'), [isLoading, setStatus]);
  useMemo(() => {
    binaries && onDataChange(binaries.list);
  }, [binaries, onDataChange]);

  const group: StudioFileItemGroup[] | undefined = useMemo(
    () =>
      binaries?.group
        .map(el => ({
          ...el,
          label: el.id,
          list: el.formattedBinaries,
        }))
        .filter(el => !!el.list.length) || undefined,
    [binaries]
  );

  const displayingOnlyVideo = useMemo(
    () => group?.length === 1 && group[0]?.label === 'binary/video',
    [group]
  );

  return (
    <FileViewer
      // TODO: to follow
      page={pageParams.page + 1}
      itemGroup={group}
      itemList={binaries?.list}
      view={view}
      selections={selections}
      goBackLabel={intl.formatMessage({id: 'dataset.viewer.go.back'})}
      gridThumbnailSize={THUMBNAIL_SIZE_GRID}
      listThumbnailSize={THUMBNAIL_SIZE_LIST}
      groupByKey={groupByKey}
      allowSelection={allowSelection}
      // Label customization
      labelHeader={labelHeader}
      labelHeaderCount={dataset?.metadataDistribution}
      onFilter={onLocalFilter}
      onSelect={onItemSelect}
      onClick={onItemClick}
      onSelectAll={onListSelectAll}
      onSort={onSort}
      virtualizedRowHeight={
        displayingOnlyVideo ? VIRTUALIZED_VIDEO_ROW_HEIGHT : VIRTUALIZED_IMAGE_ROW_HEIGHT
      }
      virtualizedColumnWidth={
        displayingOnlyVideo
          ? VIRTUALIZED_VIDEO_COLUMN_WIDTH
          : VIRTUALIZED_IMAGE_COLUMN_WIDTH
      }
      imageRenderer={props => {
        return itemRenderer({
          ...props,
          view,
          labels: labelMap[props.id],
          readOnly,
          isBusy: busyItems.includes(props.id),
          onClick: onItemClick,
        });
      }}
      gridViewRender={gridViewRender}
      listViewRender={params => listViewRender?.({...params, busyItems})}
      loadMore={onLoadMore}
      onScrollStop={onScrollStopLocal}
    />
  );
};

export const StudioDatasetView = ({children}: {children: React.ReactNode}) => {
  return <StudioDatasetViewerProvider>{children}</StudioDatasetViewerProvider>;
};
